export function initContactPageForm() {
    const REQUIRED_FIELDS_MESSAGE = 'Please complete all required fields.';
    const REQUIRED_WITH_PRIVACY_MESSAGE = 'All fields are required. Continue by accepting the Privacy Policy.';

    const forms = document.querySelectorAll('.js-contact-page-form');
    if (!forms.length) return;

    forms.forEach((form) => {
        const wrapper = form.closest('.contact-page-form');
        const status = form.querySelector('.contact-page-form__status');
        const submitButton = form.querySelector('button[type="submit"]');
        const defaultSubmitText = submitButton?.textContent || 'Submit Request';
        const requiredFields = Array.from(form.querySelectorAll('[required]')).filter((field) => field.type !== 'hidden');
        let isSubmitting = false;
        let statusTimer = null;

        const getConsentLabel = (checkbox) => {
            if (!checkbox?.id) return null;
            return form.querySelector(`label[for="${checkbox.id}"]`);
        };

        const clearFieldError = (field) => {
            field.classList.remove('is-error');
            if (field.type === 'checkbox') {
                getConsentLabel(field)?.classList.remove('is-error');
            }
        };

        const clearAllFieldErrors = () => {
            requiredFields.forEach((field) => clearFieldError(field));
        };

        const getValidationState = () => {
            const missingInputs = [];
            let missingConsent = false;

            requiredFields.forEach((field) => {
                if (field.type === 'checkbox') {
                    if (!field.checked) {
                        missingConsent = true;
                    }
                    return;
                }

                if (!String(field.value || '').trim()) {
                    missingInputs.push(field);
                }
            });

            return {
                missingInputs,
                missingConsent,
                hasErrors: missingInputs.length > 0 || missingConsent
            };
        };

        const applyValidationErrors = ({ missingInputs, missingConsent }) => {
            clearAllFieldErrors();
            missingInputs.forEach((field) => field.classList.add('is-error'));

            if (missingConsent) {
                const consentField = requiredFields.find((field) => field.type === 'checkbox');
                if (consentField) {
                    consentField.classList.add('is-error');
                    getConsentLabel(consentField)?.classList.add('is-error');
                }
            }
        };

        const getValidationMessage = ({ missingConsent }) => (
            missingConsent ? REQUIRED_WITH_PRIVACY_MESSAGE : REQUIRED_FIELDS_MESSAGE
        );

        const unlockSubmitAfterError = () => {
            if (!submitButton) return;
            submitButton.disabled = false;
            submitButton.textContent = defaultSubmitText;
        };

        const lockSubmitOnError = () => {
            if (!submitButton) return;
            submitButton.disabled = true;
            submitButton.textContent = defaultSubmitText;
        };

        const setStatus = (message, type = '') => {
            if (!status) return;
            status.textContent = message;
            status.dataset.type = type;

            if (message) {
                form.classList.add('is-status-visible');
            } else {
                form.classList.remove('is-status-visible');
            }

            if (statusTimer) {
                window.clearTimeout(statusTimer);
                statusTimer = null;
            }

            if (type === 'error') {
                lockSubmitOnError();
            } else if (!isSubmitting) {
                unlockSubmitAfterError();
            }

            if (message && type !== 'error') {
                statusTimer = window.setTimeout(() => {
                    setStatus('', '');
                }, 3000);
            }
        };

        form.addEventListener('input', () => {
            if (status?.dataset.type !== 'error') return;
            setStatus('', '');
        });

        form.addEventListener('change', () => {
            if (status?.dataset.type !== 'error') return;
            setStatus('', '');
        });

        requiredFields.forEach((field) => {
            const eventName = field.type === 'checkbox' ? 'change' : 'input';
            field.addEventListener(eventName, () => clearFieldError(field));
        });

        form.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (isSubmitting) return;

            const validation = getValidationState();
            if (validation.hasErrors) {
                applyValidationErrors(validation);
                setStatus(getValidationMessage(validation), 'error');
                return;
            }

            isSubmitting = true;
            setStatus('', '');
            clearAllFieldErrors();

            const endpoint = form.getAttribute('action') || window.ajax_var?.url || '';

            if (!endpoint) {
                setStatus('There was a problem sending your request. Please try again.', 'error');
                isSubmitting = false;
                return;
            }

            if (submitButton) {
                submitButton.disabled = true;
                submitButton.textContent = 'Sending...';
            }

            try {
                const formData = new FormData(form);
                formData.set('page_url', window.location.href);

                const response = await fetch(endpoint, {
                    method: 'POST',
                    body: formData,
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest'
                    }
                });

                const data = await response.json();

                if (!response.ok || !data.success) {
                    const message = data?.data?.message || 'There was a problem sending your request. Please try again.';
                    if (
                        message === REQUIRED_FIELDS_MESSAGE
                        || message === REQUIRED_WITH_PRIVACY_MESSAGE
                    ) {
                        const serverValidation = getValidationState();
                        if (serverValidation.hasErrors) {
                            applyValidationErrors(serverValidation);
                        }
                    }
                    setStatus(message, 'error');
                    return;
                }

                form.reset();
                clearAllFieldErrors();
                if (wrapper) {
                    wrapper.classList.add('is-success');
                    window.setTimeout(() => {
                        wrapper.classList.remove('is-success');
                    }, 3000);
                }
            } catch (error) {
                setStatus('There was a problem sending your request. Please try again.', 'error');
            } finally {
                if (submitButton && status?.dataset.type !== 'error') {
                    submitButton.disabled = false;
                    submitButton.textContent = defaultSubmitText;
                }
                isSubmitting = false;
            }
        });
    });
}
